import {
    debounce,
    groupBy,
    isEmpty,
    omitBy,
    mapValues,
    maxBy,
    minBy,
    noop,
    partial,
    pick,
    sortBy,
    sumBy,
    merge,
} from 'lodash';

import { getLogger } from './UseLog';

const canObservePerformance = !!window.PerformanceObserver;
const quietPeriod = 500;
const resourceRegex = /[^?]+/;
const postUrl = '/ngp/v2/measurements';
const log = getLogger('PerformanceLog');

let latchCount = 0;
let measures = [];
let logger = noop;

/*
 * Private functions.
 */

function summarizeEntries(includeMinMaxAvg, entries) {
    const duration = sumBy(entries, 'duration');
    return merge(
        {
            duration,
            count: entries.length,
        },
        includeMinMaxAvg
            ? {
                  min: minBy(entries, 'duration')?.duration || 0,
                  max: maxBy(entries, 'duration')?.duration || 0,
                  avg: entries.length > 0 ? duration / entries.length : 0,
              }
            : {}
    );
}

function emitMeasurements() {
    return false;
}

function postStats(page, resource, timeline) {
    const startTimes = timeline.map((item) => item.startTime);
    const endTimes = timeline.map((item) => item.endTime);
    const startTime = Math.min(...startTimes);
    const endTime = Math.max(...endTimes);
    const diff = endTime - startTime;
    const resources = timeline.filter((item) => item.entryType === 'resource');
    const totalResourceSize = resources.length ? resources.map((item) => item.transferSize).reduce((a, b) => a + b) : 0;

    if (resources.length === 0) return;

    if (!emitMeasurements()) return;

    const measurement = [
        {
            labels: {
                test: 'UIPerformance',
                npoint: 'UIPerformance',
            },
            metrics: {
                pagename: page,
                message: 'Success',
                loadtime: diff,
                loadcount: 1,
                requests: resources.length,
                responsessize: totalResourceSize,
            },
        },
    ];

    // eslint-disable-next-line react-portal/no-global-fetch
    fetch(postUrl, {
        method: 'POST',
        cache: 'no-cache',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify(measurement),
    }).then((res) => {
        if (!res.ok) log.error(`res.ok: ${res.ok}`);
    });
}

function logTable(label, table, logLevel = 'info') {
    log[logLevel](label);
    log[`${logLevel}Table`](table.data, table.columns);
}

function logMeasures(label, isPgm) {
    // Don't log if there are outstanding latches.
    if (latchCount > 0) return;

    // Summarize the measures.
    const timeline = sortBy(measures, 'startTime').map((entry) =>
        pick(
            merge(
                {
                    endTime: entry.startTime + entry.duration,
                },
                entry
            ),
            ['name', 'entryType', 'startTime', 'endTime', 'duration']
        )
    );
    // Tests without a filterState, like the new main page quickTest(s) can sometimes not have a start or end time.
    const firstMeasure = minBy(timeline, 'startTime') || 0;
    const lastMeasure = maxBy(timeline, 'endTime') || 0;
    const duration = lastMeasure.endTime - firstMeasure.startTime;
    const measuresByEntryType = groupBy(timeline, 'entryType');
    const componentEntriesByComponent = groupBy(measuresByEntryType.component, 'name');
    const resourceEntriesByResource = groupBy(measuresByEntryType.resource, (entry) => {
        return entry.name.match(resourceRegex)[0];
    });
    const summary = omitBy(
        {
            component: mapValues(componentEntriesByComponent, partial(summarizeEntries, true)),
            resource: mapValues(resourceEntriesByResource, partial(summarizeEntries, false)),
        },
        isEmpty
    );

    // Post the summary to the server so we can trend it, but not on PGMs.
    if (!isPgm) {
        const perfData = {};
        if (summary.component !== undefined) perfData['Component Summary'] = summary.component;
        if (summary.resource !== undefined) perfData['Resource Summary'] = summary.resource;
        perfData.Timeline = {
            data: timeline,
        };
        postStats(label, summary.resource, timeline);
    }

    // Log the summary in the console if logging is enabled.
    if (log.isInfoEnabled()) {
        log.info(`Performance Summary (${label}:${duration})`);
        if (summary.component !== undefined) logTable('Component Summary', { data: summary.component });
        if (summary.resource !== undefined) logTable('Resource Summary', { data: summary.resource });
        logTable(
            'Timeline',
            {
                data: timeline,
            },
            'debug'
        );
    }

    // Clear the measures.
    measures = [];
}

function addObservedMeasures(list) {
    // Exclude the performance resource requests from the measures to avoid infinite updates.
    const newMeasures = list
        .getEntries()
        .filter((entry) => !entry.name.includes(postUrl))
        .map((entry) => entry.toJSON());

    if (newMeasures.length > 0) {
        measures = [...measures, ...newMeasures];
        logger();
    }
}

if (canObservePerformance) {
    const observer = new PerformanceObserver(addObservedMeasures);
    observer.observe({
        entryTypes: ['resource'],
    });
}

/*
 * Exported functions.
 */

export function logPerformance(label, isPgm) {
    if (!canObservePerformance) return;

    log.debug(`logPerformance(${label}, ${isPgm})`);

    logger = debounce(partial(logMeasures, label, isPgm), quietPeriod);

    logger();
}

export function componentLatch(label) {
    latchCount += 1;
    log.trace(`componentLatch(${label}):  ${latchCount}`);
    return (component) => {
        latchCount -= 1;
        log.trace(`componentLatch(${label}).done():  ${latchCount}`);
        return component;
    };
}

export function addComponentMeasure(component, mode, duration, baseTime, startTime, commitTime) {
    if (!canObservePerformance || duration === 0) return;
    measures = [
        ...measures,
        {
            entryType: 'component',
            name: component,
            startTime,
            duration,
            mode,
            baseTime,
            commitTime,
        },
    ];
    logger();
}

export function apiLatch(label) {
    latchCount += 1;
    log.trace(`apiLatch(${label}):  ${latchCount}`);
    return () => {
        latchCount -= 1;
        log.trace(`apiLatch(${label}).done():  ${latchCount}`);
        logger();
    };
}

export default {
    logPerformance,
    componentLatch,
    apiLatch,
};
